// src/components/classroom/TeacherDashboard.js
import React, { useState } from 'react';
import styled from 'styled-components';
import RosterPanel from './RosterPanel';
import WhiteboardCanvas from '../whiteboard/WhiteboardCanvas';
import EnhancedWhiteboardCanvas from '../whiteboard/EnhancedWhiteboardCanvas';
import DynamicFunctionGraphing from '../whiteboard/DynamicFunctionGraphing';
import TeachingMaterialsLibrary from '../whiteboard/TeachingMaterialsLibrary';
import WhiteboardSettings from '../settings/WhiteboardSettings';
import ToolbarPanel from './ToolbarPanel';
import VideoPanel from './VideoPanel';
import { useDevice } from '../../contexts/DeviceContext';
import { Calculator, BookOpen, Grid, TrendingUp } from 'lucide-react';

const DashboardContainer = styled.div`
  display: grid;
  grid-template-areas: 
    ${props => props.videoVisible ? '"video video toolbar"' : '"toolbar toolbar toolbar"'}
    ${props => props.materialsVisible ? '"materials whiteboard roster"' : props.rosterVisible ? '"whiteboard whiteboard roster"' : '"whiteboard whiteboard whiteboard"'};
  grid-template-columns: ${props => {
    if (props.materialsVisible && props.rosterVisible) return '1000px 1fr 300px';
    if (props.materialsVisible) return '1000px 1fr 0px';
    if (props.rosterVisible) return '0px 1fr 300px';
    return '0px 1fr 0px';
  }};
  grid-template-rows: ${props => props.videoVisible ? 'auto 1fr auto' : 'auto 1fr auto'};
  height: 100vh;
  background: #1a1a1a;
  position: relative;
  transition: all 0.3s ease;
  
  @media (max-width: 1024px) {
    grid-template-areas: 
      ${props => props.videoVisible ? '"video"' : '""'}
      "toolbar"
      "whiteboard"
      ${props => props.rosterVisible ? '"roster"' : '""'};
    grid-template-columns: 1fr;
    grid-template-rows: ${props => props.videoVisible ? 'auto auto 1fr auto' : 'auto 1fr auto'};
  }
`;

const VideoContainer = styled.div`
  grid-area: video;
  display: flex;
  justify-content: center;
  padding: 10px;
  background: #2a2a2a;
`;

const WhiteboardContainer = styled.div`
  grid-area: whiteboard;
  background: white;
  position: relative;
  overflow: hidden;
`;

const RosterContainer = styled.div`
  grid-area: roster;
  background: #2a2a2a;
  border-left: 1px solid #444;
`;

const MaterialsContainer = styled.div`
  grid-area: materials;
  background: #2a2a2a;
  border-right: 1px solid #444;
  overflow-y: auto;
  width: 1000px; /* Doubled width for maximum content display */
`;

const ToolbarContainer = styled.div`
  grid-area: toolbar;
  background: #333;
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModeSelector = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const ModeButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: ${props => props.active ? '#3b82f6' : '#374151'};
  color: white;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#2563eb' : '#4b5563'};
  }
`;

const TeacherDashboard = () => {
  const { isMobile, isTablet } = useDevice();
  const [currentTool, setCurrentTool] = useState('pen');
  const [currentMode, setCurrentMode] = useState('whiteboard'); // 'whiteboard', 'graphing', 'materials'
  const [showRoster, setShowRoster] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [whiteboardSettings, setWhiteboardSettings] = useState({
    selectedBackground: '#000000',
    textSize: 16,
    textColor: '#ffffff',
    textStyle: {
      bold: false,
      italic: false,
      underline: false
    },
    videoEnabled: true,
    panelsVisible: {
      left: true,
      right: true,
      bottom: true,
      top: true
    }
  });
  const [panelVisibility, setPanelVisibility] = useState({
    materials: false,
    roster: false,
    video: false,
    settings: false
  });

  const handleAddMaterialToCanvas = (material) => {
    // This would integrate with the whiteboard to add the material
    console.log('Adding material to canvas:', material);
  };

  const handleSettingsChange = (newSettings) => {
    setWhiteboardSettings(newSettings);
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleToggleMaterialsPanel = () => {
    setPanelVisibility(prev => ({
      ...prev,
      materials: !prev.materials
    }));
  };

  const handleToggleRosterPanel = () => {
    setPanelVisibility(prev => ({
      ...prev,
      roster: !prev.roster
    }));
  };

  const handleToggleVideoPanel = () => {
    setPanelVisibility(prev => ({
      ...prev,
      video: !prev.video
    }));
  };

  const handleToggleSettingsPanel = () => {
    setPanelVisibility(prev => ({
      ...prev,
      settings: !prev.settings
    }));
  };

  const renderMainContent = () => {
    switch (currentMode) {
      case 'graphing':
        return <DynamicFunctionGraphing />;
      case 'materials':
        return <TeachingMaterialsLibrary onAddToCanvas={handleAddMaterialToCanvas} />;
      default:
        return (
          <EnhancedWhiteboardCanvas 
            currentTool={currentTool}
            settings={whiteboardSettings}
            onSettingsChange={handleSettingsChange}
            isFullscreen={isFullscreen}
            onToggleFullscreen={handleToggleFullscreen}
            onOpenSettings={() => setPanelVisibility(prev => ({ ...prev, settings: true }))}
            onToggleMaterialsPanel={handleToggleMaterialsPanel}
            onToggleRosterPanel={handleToggleRosterPanel}
            onToggleVideoPanel={handleToggleVideoPanel}
            onToggleSettingsPanel={handleToggleSettingsPanel}
          />
        );
    }
  };

  return (
    <DashboardContainer 
      videoVisible={panelVisibility.video}
      materialsVisible={panelVisibility.materials}
      rosterVisible={panelVisibility.roster}
    >
      {/* Video Panel - controlled by panelVisibility.video */}
      {panelVisibility.video && (
        <VideoContainer>
          <VideoPanel />
        </VideoContainer>
      )}
      
      {/* Toolbar Container - always visible */}
      <ToolbarContainer>
        <ToolbarPanel 
          currentTool={currentTool}
          setCurrentTool={setCurrentTool}
          showRoster={showRoster}
          setShowRoster={setShowRoster}
        />
      </ToolbarContainer>
      
      <WhiteboardContainer>
        {renderMainContent()}
      </WhiteboardContainer>
      
      {/* Materials Panel - controlled by panelVisibility.materials */}
      {panelVisibility.materials && (
        <MaterialsContainer>
          <TeachingMaterialsLibrary onAddToCanvas={handleAddMaterialToCanvas} />
        </MaterialsContainer>
      )}

      {/* Roster Panel - controlled by panelVisibility.roster */}
      {panelVisibility.roster && (
        <RosterContainer>
          <RosterPanel />
        </RosterContainer>
      )}
      
      {/* Settings Panel - controlled by panelVisibility.settings */}
      <WhiteboardSettings 
        isOpen={panelVisibility.settings}
        onClose={() => setPanelVisibility(prev => ({ ...prev, settings: false }))}
        onSettingsChange={handleSettingsChange}
      />

    </DashboardContainer>
  );
};

export default TeacherDashboard;